/**
 * PWA-установка: ранний перехват `beforeinstallprompt` (Chromium) + хелперы для своего баннера
 * и инструкции для iOS. Слушатели вешаются на модульном уровне (модуль импортируется в
 * index.tsx), чтобы не пропустить событие, которое браузер может прислать до маунта React.
 */
import { useSyncExternalStore } from 'react';

interface IBeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const DISMISS_KEY = 'moni-pwa-dismissed';
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 дней не показываем после отказа

let deferredPrompt: IBeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Гасим браузерный мини-инфобар — покажем своё предложение.
    event.preventDefault();
    deferredPrompt = event as IBeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit();
  });
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Доступна ли программная установка прямо сейчас (Chromium прислал `beforeinstallprompt`). */
export function useCanInstall(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => deferredPrompt !== null,
    () => false
  );
}

/** Показать нативный диалог установки. Возвращает исход или null, если событие недоступно. */
export async function promptInstall(): Promise<
  'accepted' | 'dismissed' | null
> {
  if (!deferredPrompt) return null;
  await deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  emit();
  return outcome;
}

/** Приложение уже открыто как установленное (standalone) — предлагать установку не нужно. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean })
    .standalone;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    iosStandalone === true
  );
}

/** iOS — программной установки нет, показываем инструкцию «Поделиться → На экран „Домой"». */
export function isIos(): boolean {
  if (typeof window === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

/** Отказ от установки запомнен и ещё «свежий» (не надоедаем повторным баннером). */
export function isInstallDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    return !!raw && Date.now() - Number(raw) < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

/** Запомнить отказ (баннер не покажется ближайшие DISMISS_TTL_MS). */
export function dismissInstall(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // localStorage недоступен — баннер просто появится в следующий раз
  }
}

/**
 * Регистрация service worker (устанавливаемость PWA + рантайм-кэш ассетов).
 * Только прод: в dev кэширующий SW мешает HMR и отдаёт устаревшие файлы.
 * Стратегии кэша — в public/sw.js.
 */
export function registerServiceWorker(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  if (process.env.NODE_ENV !== 'production') return;
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}
